import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Animated, Easing, Platform, useWindowDimensions } from 'react-native';
import LinearGradient from '../../../components/LinearGradient';
import { Coins, Gift, LogOut, PlayCircle, Pause, Play } from '../../../components/Icons';
import { VaultChestIcon } from './RelicVault/shared/RelicVaultIcons';
import { useRemScale } from '../../../lib/remScale';
import { UpgradePill } from '../../../components/subscription/WorldNotices';
import { hudMode } from './hudLayout';

import { TYPE, FONTS } from 'style/typography';
import { SIMPLIFIED_FIRST_VISIT } from '../../../config/app.config';
import { guardTourPress, on as onFirstVisit, tourHideActive } from '../../../firstVisit/events';

// NOTE: For web/desktop, we use CSS backdrop-filter instead of native BlurView
// since @react-native-community/blur doesn't work on web.

const SHIMMER_WIDTH = 120;

/**
 * Meeting top HUD bar:
 * - Trophy/Leaderboard button on the left
 * - Combined Rewards Store + QuikCoins trigger (single pill) in center
 * - "All Lessons and Quizzes", which opens the lesson menu (and pauses a playing lesson first)
 * - "Upgrade" for Free learners, which opens the full-screen plans in place (owner 2026-09-29); in
 *   quikbase it sits beside the Collection Store, since learners land there first
 * - Exit button on the right (shows "Exit Lesson" when lesson is active)
 * Narrower windows (small desktops, browser zoom) use the 'compact' and 'stacked' layouts of ./hudLayout.
 */
const TopHudBar = ({
  worldLocation,
  canShowLessonButton,
  canPressLessonButton,
  onPressLessonButton,
  onPressExit,
  onOpenRewardsStore,
  onOpenRelicVault,
  quikcoinsBalance,
  lessonStarted = false,
  hudLoading = false,
  onCenterGroupLayout,
  onUpgrade,
}) => {
  const { rem } = useRemScale();
  const { width: windowWidth } = useWindowDimensions();
  // Web only: native (phones in landscape) keeps the original layout; mobile UI is out of scope.
  const mode = Platform.OS === 'web' ? hudMode(windowWidth) : 'wide';
  const wide = mode === 'wide';

  // Reference tokens: px-3 pt-3 (12), gap-3 (12), p-3 (12), h-10 (40), rounded-xl (12)
  const topInset = rem(12);
  const sideInset = rem(12);
  const groupGap = rem(12);
  const groupPad = rem(12);
  const controlH = rem(40);
  const radius = rem(12);

  const shimmerAnim = useRef(new Animated.Value(-1)).current;
  const shimmerTranslate = shimmerAnim.interpolate({
    inputRange: [-1, 1],
    outputRange: [-SHIMMER_WIDTH, SHIMMER_WIDTH + SHIMMER_WIDTH],
  });

  const lessonScale = useRef(new Animated.Value(1)).current;
  const exitScale = useRef(new Animated.Value(1)).current;
  const rewardsScale = useRef(new Animated.Value(1)).current;
  const relicVaultScale = useRef(new Animated.Value(1)).current;

  const showHudLoading = Boolean(hudLoading);

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 2,
        duration: 2500,
        easing: Easing.linear,
        useNativeDriver: false,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmerAnim]);

  const isQuikbase = worldLocation === 'quikbase';
  // A unit world's exit always reads "Leave unit", in today's red (owner round 13.14; Sol round13 R3 A); Quikbase keeps
  // the icon-only Exit. Elsewhere it reads "Exit Lesson" while a lesson plays, as before.
  const exitLabel = SIMPLIFIED_FIRST_VISIT ? 'Leave unit' : lessonStarted ? 'Exit Lesson' : null;
  const [tourHide, setTourHide] = useState(tourHideActive());
  useEffect(() => onFirstVisit('tour-hide', setTourHide), []);
  // The simplified first visit drops the Rewards Store / QuikCoins pill (reviewer item 13): coins live in the Collection
  // Store. Off in production until that rollout.
  const showRewardsCoins = !isQuikbase && !SIMPLIFIED_FIRST_VISIT;
  const showLesson = !isQuikbase && !!canShowLessonButton;
  // The Relic Vault (relics + guardian companions) is available in quikbase as
  // well as the unit metaverse - the companion follows the avatar everywhere.
  const showRelicVault = !!onOpenRelicVault;

  // Styles using CSS backdrop-filter for web
  const webBlurStyle = Platform.OS === 'web' ? {
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  } : {};

  const skeletonBaseColor = 'rgba(255,255,255,0.08)';
  const skeletonBorderColor = 'rgba(255,255,255,0.12)';
  const shimmerColors = [
    'rgba(255,255,255,0.05)',
    'rgba(255,255,255,0.35)',
    'rgba(255,255,255,0.05)',
  ];
  const highlightBorderColor = 'rgba(197,232,54,0.30)';
  // One button for every lesson and quiz (owner 2026-10-09): it always opens the lesson menu, pausing a lesson
  // that is playing (Sol player-tour Q3 A); pause and resume live on the caption bar.
  const lessonLabel = 'All Lessons and Quizzes';
  const quikcoinsDisplay = String(quikcoinsBalance ?? 0);

  const rewardsPillStyle = {
    height: controlH,
    paddingHorizontal: rem(12),
    borderRadius: radius,
    backgroundColor: 'rgba(15,16,20,0.86)',
    borderWidth: 1,
    borderColor: highlightBorderColor,
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: rem(12),
    shadowOffset: { width: 0, height: rem(6) },
    flexDirection: 'row',
    alignItems: 'center',
  };

  const lessonPillStyle = {
    height: controlH,
    paddingHorizontal: rem(16),
    borderRadius: radius,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(251,191,36,0.70)',
    shadowColor: '#000',
    shadowOpacity: 0.40,
    shadowRadius: rem(12),
    shadowOffset: { width: 0, height: rem(6) },
    minWidth: rem(110),
  };

  const rewardsSeparatorStyle = {
    marginHorizontal: rem(8),
    color: 'rgba(197,232,54,0.45)',
    fontWeight: '800',
    fontFamily: FONTS.body,
  };

  const rewardsValueStyle = {
    marginLeft: rem(8),
    color: '#C5E836',
    fontSize: rem(TYPE.sm),
    fontFamily: FONTS.body,
    fontWeight: '800',
    letterSpacing: 0.3,
  };

  const lessonTextStyle = {
    marginLeft: rem(8),
    color: '#000',
    fontWeight: '800',
    fontSize: rem(TYPE.sm),
    fontFamily: FONTS.body,
    letterSpacing: 0.4,
  };

  // Shared between the quikbase HUD and the unit-metaverse centre group.
  const renderRelicVaultButton = ({ marginLeft = 0 } = {}) => {
    if (!showRelicVault) return null;
    return (
      <Animated.View style={{ transform: [{ scale: relicVaultScale }], marginLeft }}>
        <TouchableOpacity
          activeOpacity={0.95}
          onPressIn={() => {
            Animated.spring(relicVaultScale, {
              toValue: 0.95,
              useNativeDriver: true,
              friction: 6,
            }).start();
          }}
          onPressOut={() => {
            Animated.spring(relicVaultScale, {
              toValue: 1,
              useNativeDriver: true,
              friction: 6,
            }).start();
          }}
          onPress={guardTourPress('hud-collection-store', onOpenRelicVault)}
          testID="hud-collection-store"
          accessibilityRole="button"
          accessibilityLabel="Open the Collection Store"
          style={{
            width: controlH,
            height: controlH,
            borderRadius: radius,
            backgroundColor: 'rgba(15,16,20,0.86)',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: highlightBorderColor,
            shadowColor: '#000',
            shadowOpacity: 0.35,
            shadowRadius: rem(12),
            shadowOffset: { width: 0, height: rem(6) },
          }}
        >
          <VaultChestIcon size={rem(20)} />
        </TouchableOpacity>
      </Animated.View>
    );
  };

  // In quikbase: Upgrade (Free), Relic Vault and Exit.
  if (isQuikbase) {
    return (
      <View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          top: topInset,
          right: sideInset,
          zIndex: 50,
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {onUpgrade ? (
          <UpgradePill
            height={controlH}
            radius={radius}
            onPress={onUpgrade}
            style={{ marginRight: showRelicVault ? groupGap : 0 }}
          />
        ) : null}
        {renderRelicVaultButton()}
        <Animated.View
          style={{
            transform: [{ scale: exitScale }],
            marginLeft: showRelicVault || onUpgrade ? groupGap : 0,
          }}
        >
          <TouchableOpacity
            activeOpacity={0.95}
            onPressIn={() => {
              Animated.spring(exitScale, {
                toValue: 0.95,
                useNativeDriver: true,
                friction: 6,
              }).start();
            }}
            onPressOut={() => {
              Animated.spring(exitScale, {
                toValue: 1,
                useNativeDriver: true,
                friction: 6,
              }).start();
            }}
            onPress={guardTourPress('hud-exit', onPressExit)}
            testID="hud-exit"
            accessibilityRole="button"
            accessibilityLabel="Exit"
            style={{
              width: controlH,
              height: controlH,
              borderRadius: radius,
              backgroundColor: '#ef4444',
              shadowColor: '#000',
              shadowOpacity: 0.45,
              shadowRadius: rem(12),
              shadowOffset: { width: 0, height: rem(6) },
              elevation: Platform.OS === 'android' ? 10 : 0,
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: 'rgba(239,68,68,0.30)',
            }}
          >
            <LogOut size={rem(18)} color="#fff" strokeWidth={2.5} />
          </TouchableOpacity>
        </Animated.View>
      </View>
    );
  }

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        top: topInset,
        left: sideInset,
        right: sideInset,
        zIndex: 50,
        flexDirection: 'row',
        // wide: the group is centred and Exit is pinned right; otherwise both sit right, Exit in the row's flow
        alignItems: mode === 'stacked' ? 'flex-start' : 'center',
        justifyContent: wide ? 'center' : 'flex-end',
      }}
    >
      {/* Center: Rewards/Coins + Lesson control */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: radius,
          backgroundColor: 'transparent',
          padding: groupPad,
          ...(mode === 'stacked'
            ? { flexShrink: 1, flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: groupGap }
            : null),
        }}
        onLayout={event => {
          if (typeof onCenterGroupLayout !== 'function') return;
          const layout = event?.nativeEvent?.layout;
          if (!layout) return;
          const left = sideInset + (layout.x || 0);
          const measuredWidth = layout.width || 0;
          onCenterGroupLayout({
            left,
            width: measuredWidth,
            right: left + measuredWidth,
            bottom: topInset + (layout.y || 0) + (layout.height || 0),
            mode,
          });
        }}
      >
        {/* Rewards Store + Coins (combined trigger) */}
        {showHudLoading ? (
          <>
            <View
              pointerEvents="none"
              style={[
                rewardsPillStyle,
                {
                  backgroundColor: skeletonBaseColor,
                  borderColor: skeletonBorderColor,
                  overflow: 'hidden',
                },
              ]}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', opacity: 0 }}>
                <Gift size={rem(16)} color="#C5E836" strokeWidth={2.5} />
                <Text style={rewardsSeparatorStyle}>|</Text>
                <Coins size={rem(16)} color="#C5E836" strokeWidth={2.5} />
                <Text
                  style={rewardsValueStyle}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {quikcoinsDisplay}
                </Text>
              </View>
              <Animated.View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: SHIMMER_WIDTH,
                  transform: [{ translateX: shimmerTranslate }, { rotate: '-45deg' }],
                  opacity: 0.35,
                }}
              >
                <LinearGradient
                  colors={shimmerColors}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{ flex: 1 }}
                />
              </Animated.View>
            </View>
            <View
              pointerEvents="none"
              style={[
                lessonPillStyle,
                {
                  marginLeft: groupGap,
                  backgroundColor: skeletonBaseColor,
                  borderColor: skeletonBorderColor,
                  overflow: 'hidden',
                },
              ]}
            >
              <Text style={[lessonTextStyle, { opacity: 0 }]}>{lessonLabel}</Text>
              <Animated.View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: SHIMMER_WIDTH,
                  transform: [{ translateX: shimmerTranslate }, { rotate: '-45deg' }],
                  opacity: 0.35,
                }}
              >
                <LinearGradient
                  colors={shimmerColors}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{ flex: 1 }}
                />
              </Animated.View>
            </View>
          </>
        ) : (
          <>
            {showRewardsCoins && (
              <Animated.View style={{ transform: [{ scale: rewardsScale }] }}>
                <TouchableOpacity
                  activeOpacity={0.95}
                  onPressIn={() => {
                    Animated.spring(rewardsScale, {
                      toValue: 0.95,
                      useNativeDriver: true,
                      friction: 6,
                    }).start();
                  }}
                  onPressOut={() => {
                    Animated.spring(rewardsScale, {
                      toValue: 1,
                      useNativeDriver: true,
                      friction: 6,
                    }).start();
                  }}
                  onPress={onOpenRewardsStore}
                  accessibilityRole="button"
                  accessibilityLabel="Open Rewards Store"
                  style={rewardsPillStyle}
                >
                  <Gift size={rem(16)} color="#C5E836" strokeWidth={2.5} />
                  <Text style={rewardsSeparatorStyle}>|</Text>
                  <Coins size={rem(16)} color="#C5E836" strokeWidth={2.5} />
                  <Text
                    style={rewardsValueStyle}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {quikcoinsDisplay}
                  </Text>
                </TouchableOpacity>
              </Animated.View>
            )}

            {/* All Lessons and Quizzes: opens the lesson menu (pausing a playing lesson) */}
            {showLesson && (
              <Animated.View style={{ transform: [{ scale: lessonScale }], marginLeft: showRewardsCoins ? groupGap : 0 }}>
                <TouchableOpacity
                  activeOpacity={0.95}
                  disabled={!canPressLessonButton}
                  onPressIn={() => {
                    if (!canPressLessonButton) return;
                    Animated.spring(lessonScale, {
                      toValue: 0.95,
                      useNativeDriver: true,
                      friction: 6,
                    }).start();
                  }}
                  onPressOut={() => {
                    Animated.spring(lessonScale, {
                      toValue: 1,
                      useNativeDriver: true,
                      friction: 6,
                    }).start();
                  }}
                  onPress={guardTourPress('hud-lessons', onPressLessonButton)}
                  testID="hud-lessons"
                  accessibilityRole="button"
                  accessibilityLabel={lessonLabel}
                  style={{
                    borderRadius: radius,
                    overflow: 'hidden',
                    opacity: canPressLessonButton ? 1 : 0.4,
                  }}
                >
                  <LinearGradient
                    colors={['#fbbf24', '#fde68a', '#fbbf24']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={lessonPillStyle}
                  >
                    {/* shimmer - only show when lesson not started */}
                    {!lessonStarted && (
                      <Animated.View
                        pointerEvents="none"
                        style={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          width: SHIMMER_WIDTH,
                          transform: [{ translateX: shimmerTranslate }, { rotate: '-45deg' }],
                          opacity: 0.35,
                        }}
                      >
                        <LinearGradient
                          colors={[
                            'rgba(255,255,255,0.10)',
                            'rgba(255,255,255,0.80)',
                            'rgba(255,255,255,0.10)',
                          ]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                          style={{ flex: 1 }}
                        />
                      </Animated.View>
                    )}

                    <Text style={lessonTextStyle}>{lessonLabel}</Text>
                  </LinearGradient>
                </TouchableOpacity>
              </Animated.View>
            )}

            {onUpgrade && !tourHide ? (
              <UpgradePill
                height={controlH}
                radius={radius}
                onPress={onUpgrade}
                compact={!wide}
                style={{ marginLeft: showRewardsCoins || showLesson ? groupGap : 0 }}
              />
            ) : null}

            {/* Collection Store */}
            {renderRelicVaultButton({
              marginLeft: showRewardsCoins || showLesson || onUpgrade ? groupGap : 0,
            })}
          </>
        )}
      </View>

      {tourHide ? null : (
      <>
      {/* Right: Exit - pinned to the right edge when wide, otherwise next to the group (never under it) */}
      {/* When lesson is active, show "Exit Lesson" button with text label */}
      <Animated.View style={{
        transform: [{ scale: exitScale }],
        ...(wide
          ? { position: 'absolute', right: 0 }
          : { flexShrink: 0, marginTop: mode === 'stacked' ? groupPad : 0 }),
      }}>
        <TouchableOpacity
          activeOpacity={0.95}
          onPressIn={() => {
            Animated.spring(exitScale, {
              toValue: 0.95,
              useNativeDriver: true,
              friction: 6,
            }).start();
          }}
          onPressOut={() => {
            Animated.spring(exitScale, {
              toValue: 1,
              useNativeDriver: true,
              friction: 6,
            }).start();
          }}
          onPress={guardTourPress('hud-exit', onPressExit)}
          testID="hud-exit"
          accessibilityRole="button"
          accessibilityLabel={exitLabel || 'Exit'}
          style={exitLabel ? {
            height: controlH,
            paddingHorizontal: rem(16),
            borderRadius: radius,
            backgroundColor: '#ef4444',
            shadowColor: '#000',
            shadowOpacity: 0.45,
            shadowRadius: rem(12),
            shadowOffset: { width: 0, height: rem(6) },
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            borderWidth: 1,
            borderColor: 'rgba(239,68,68,0.30)',
          } : {
            width: controlH,
            height: controlH,
            borderRadius: radius,
            backgroundColor: '#ef4444',
            shadowColor: '#000',
            shadowOpacity: 0.45,
            shadowRadius: rem(12),
            shadowOffset: { width: 0, height: rem(6) },
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: 'rgba(239,68,68,0.30)',
          }}
        >
          <LogOut size={rem(18)} color="#fff" strokeWidth={2.5} />
          {exitLabel ? (
            <Text
              style={{
                marginLeft: rem(8),
                color: '#fff',
                fontWeight: '700',
                fontSize: rem(TYPE.sm),
                fontFamily: FONTS.body,
              }}
            >
              {exitLabel}
            </Text>
          ) : null}
        </TouchableOpacity>
      </Animated.View>
      </>
      )}
    </View>
  );
};


export default TopHudBar;
